import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react'
import { Box } from '@mui/material'
import {
  //  EmtDistance,
  EmtTypography,
} from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { isUserOnMobile } from 'emt-library'

import { ServiceSection } from '../reservations/NewRequest/ServiceSection'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { getDefaultSearchDistance } from '@/helpers/search/searchFilters'

import {
  ServiceSlug,
  getSlotType,
  makeBlankSlot,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'
import { useServiceSlots } from '@/hooks/useServiceSlots'
// import { useDisplayDistance } from '@/hooks'
import AddressAutocomplete from '@/components/EMT/AddressAutocomplete'
import AnimalIncrement from '@/components/PetSitter/AnimalIncrement'
import useCurrentUser from '@/hooks/useCurrentUser'

type Props = {
  isMobile: boolean
  selectedService: string | null
  formRefs: React.MutableRefObject<Record<string, any>>
  // ⚠️ in this flow it's { selectedService, formSlots } (wrapper), not actual form initial values
  localStorageInitialValues: any
  onServiceChange: (value: string) => void
  serviceSectionRef?: React.RefObject<HTMLDivElement>
}

const NewSearchForm = forwardRef<any, Props>(
  ({ isMobile, selectedService, formRefs, localStorageInitialValues, onServiceChange, serviceSectionRef }, ref) => {
    const t = useTranslation('petsitter').t
    const { currentUser } = useCurrentUser()
    // const displayDistance = useDisplayDistance()
    const [, setIsFocus] = useState(false)

    const handleIsFocus = (value: boolean) => {
      if (!isUserOnMobile()) return
      setIsFocus(value)
    }

    // ✅ NEW STORE (cart)
    const hasHydrated = useNewRequestCartStore((s) => s.hasHydrated)

    const address = useNewRequestCartStore((s) => s.address) || currentUser?.settings.address
    const searchParams = {
      address,
    }
    const handleSearchParamsChange = (changes: Partial<typeof searchParams>) => {
      if (changes.address !== undefined) {
        useNewRequestCartStore.getState().setAddress(changes.address)
        // Remove address error if address is now valid
        if (changes.address && 'formatted' in changes.address && changes.address.formatted && errors.address) {
          setErrors((prevErrors) => {
            const { address, ...rest } = prevErrors
            return rest
          })
        }
      }
    }

    const distance = useNewRequestCartStore((s) => s.distance)
    const setDistance = useNewRequestCartStore((s) => s.setDistance)

    const cartSelectedService = useNewRequestCartStore((s) => s.selectedService)
    const setSelectedService = useNewRequestCartStore((s) => s.setSelectedService)

    const petsToComplete = useNewRequestCartStore((s) => s.petsToComplete)
    const setPetsToComplete = useNewRequestCartStore((s) => s.setPetsToComplete)
    const setAnimals = useNewRequestCartStore((s) => s.setAnimals)

    useEffect(() => {
      setDistance(getDefaultSearchDistance(selectedService))
    }, [selectedService])

    // Local state for animal counts (dog, cat, nac)
    const [animalCounts, setAnimalCounts] = useState(() => {
      // Initialize from store if available
      const initial: Record<'dog' | 'cat' | 'nac', number> = { dog: 0, cat: 0, nac: 0 }
      petsToComplete.forEach((p) => {
        initial[p.slug] = p.number
      })
      return initial
    })

    const handleAnimalChange = (slug: 'dog' | 'cat' | 'nac', delta: number) => {
      setAnimalCounts((prev) => {
        const next = { ...prev, [slug]: Math.max(0, prev[slug] + delta) }
        // Remove petsToComplete error if at least one pet is selected
        if (next.dog + next.cat + next.nac > 0 && errors.petsToComplete) {
          setErrors((prevErrors) => {
            const { petsToComplete, ...rest } = prevErrors
            return rest
          })
        }
        return next
      })
    }

    const actualService = cartSelectedService || selectedService

    // Shared slot logic
    const { formSlotsByService, updateFormSlotsByService, currentSlug, addNewSlot, handleSaveSlot, removeSlot } =
      useServiceSlots(actualService)

    const [errors, setErrors] = useState<Record<string, string>>({})

    // Ensure we always have at least one slot for the active service (after hydration)
    useEffect(() => {
      if (!hasHydrated) return
      if (!currentSlug) return
      const currentSlots = formSlotsByService?.[currentSlug] ?? []
      if (currentSlots.length > 0) return
      updateFormSlotsByService((prev) => ({
        ...prev,
        [currentSlug]: [makeBlankSlot(currentSlug, null)],
      }))
    }, [hasHydrated, currentSlug, formSlotsByService, updateFormSlotsByService])

    // wrap service change so reset happens ONLY on user action
    const handleServiceChange = (value: string) => {
      onServiceChange(value)
      const slug = getSlotType(value) as ServiceSlug | null
      if (!slug) return
      setSelectedService(slug)
      // const isRecurrent = slug === 'promenade' || slug === 'visite'
      updateFormSlotsByService((prev) => {
        const existing = prev?.[slug] ?? []
        const nextSlots =
          // isRecurrent
          //   ? [makeBlankSlot(slug, null)]
          //   :
          existing.length ? existing : [makeBlankSlot(slug, null)]

        return { ...prev, [slug]: nextSlots }
      })
    }

    useImperativeHandle(ref, () => ({
      async validate() {
        let hasError = false
        const newErrors: Record<string, string> = {}

        if (!currentSlug) {
          newErrors.selectedService = 'Veuillez sélectionner un service.'
          hasError = true
        }

        // Check at least one pet
        const totalPets = animalCounts.dog + animalCounts.cat + animalCounts.nac
        if (totalPets === 0) {
          newErrors.petsToComplete = 'Veuillez sélectionner au moins un animal.'
          hasError = true
        }

        // Check localisation
        if (!address || !('formatted' in address) || !address.formatted) {
          newErrors.address = 'Veuillez indiquer une adresse de recherche.'
          hasError = true
        }

        let allOK = true
        if (currentSlug) {
          for (const slot of formSlotsByService[currentSlug] ?? []) {
            const slotRef = formRefs.current[slot.id]
            // DateTimeForm and DateTimeFormRecurrent must both expose validateOnly()
            if (slotRef?.validateOnly) {
              const ok = slotRef.validateOnly()
              if (!ok) allOK = false
            }
          }
        }

        // On validate, update store with animal counts
        setPetsToComplete([
          { slug: 'dog', number: animalCounts.dog },
          { slug: 'cat', number: animalCounts.cat },
          { slug: 'nac', number: animalCounts.nac },
        ])
        setAnimals([])

        if (!allOK) hasError = true

        setErrors(newErrors)
        return !hasError
      },

      getValues() {
        return {
          selectedService: currentSlug,
          formSlots: currentSlug ? formSlotsByService[currentSlug] : [],
          distance,
          address,
          petsToComplete: [
            { slug: 'dog', number: animalCounts.dog },
            { slug: 'cat', number: animalCounts.cat },
            { slug: 'nac', number: animalCounts.nac },
          ],
          period: {
            start: formSlotsByService[currentSlug ?? '']?.[0]?.data?.dateStart ?? null,
            end: formSlotsByService[currentSlug ?? '']?.[0]?.data?.dateEnd ?? null,
          },
        }
      },
    }))

    // Distance mapping
    // const distanceCorrespondingValues = {
    //   percentToValue: { 0: 0, 10: 1, 20: 2, 30: 3, 40: 4, 50: 5, 60: 10, 70: 15, 80: 20, 90: 30, 100: 50 },
    //   valueToPercent: { 0: 0, 1: 10, 2: 20, 3: 30, 4: 40, 5: 50, 10: 60, 15: 70, 20: 80, 30: 90, 50: 100 },
    // }

    // const [distancePercent, setDistancePercent] = useState<[number, number]>([
    //   distanceCorrespondingValues.valueToPercent[distance.min],
    //   distanceCorrespondingValues.valueToPercent[distance.max],
    // ])

    // ✅ keep slider UI synced if store changes from outside
    // useEffect(() => {
    //   setDistancePercent([
    //     distanceCorrespondingValues.valueToPercent[distance.min],
    //     distanceCorrespondingValues.valueToPercent[distance.max],
    //   ])
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [distance.min, distance.max])

    // const handleDistanceLiveChange = (newValue: number | number[]) => {
    //   if (!Array.isArray(newValue)) return
    //   setDistancePercent(newValue as [number, number]) // ✅ UI only
    // }

    // const commitDistance = (newValue: number | number[]) => {
    //   if (!Array.isArray(newValue)) return
    //   const [minP, maxP] = newValue as [number, number]

    //   setDistance({
    //     min: distanceCorrespondingValues.percentToValue[minP],
    //     max: distanceCorrespondingValues.percentToValue[maxP],
    //   })
    // }

    // const isIOS =
    //   typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

    return (
      <Box
        ref={serviceSectionRef}
        sx={{
          display: isMobile ? 'flex' : 'grid',
          gridTemplateColumns: '5fr 6fr',
          gap: '20px',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {!isMobile && (
              <EmtTypography variant="body1" fontWeight="700" sx={{ fontSize: '18px' }}>
                De quel service avez-vous besoin ?
              </EmtTypography>
            )}

            <ServiceSection
              isMobile={isMobile}
              selectedService={actualService}
              errors={errors}
              // @ts-expect-error
              formSlotsByService={formSlotsByService}
              formRefs={formRefs}
              // ✅ important fix: don't pass wrapper object here
              localStorageInitialValues={null}
              onServiceChange={handleServiceChange}
              onAddNewSlot={addNewSlot}
              onSaveSlot={handleSaveSlot}
              onRemoveSlot={removeSlot}
              showSelect
              addButton={false}
              showGardeTimes={false}
            />

            {errors.selectedService && (
              <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                {errors.selectedService}
              </EmtTypography>
            )}
            {errors.dateTimeInstances && (
              <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                {errors.dateTimeInstances}
              </EmtTypography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <EmtTypography variant="body1" fontWeight="700" sx={{ fontSize: '18px' }}>
              A quelle adresse ?
            </EmtTypography>

            <AddressAutocomplete
              label={t('search.location')}
              address={searchParams.address && 'formatted' in searchParams.address ? searchParams.address : null}
              onAddressChange={(address) => handleSearchParamsChange({ address })}
              onFocus={() => handleIsFocus(true)}
              onBlur={() => handleIsFocus(false)}
              iconLeftClassName="emt-pin"
            />

            {errors.address && (
              <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                {errors.address}
              </EmtTypography>
            )}

            {/* {actualService === 'garde-chez-eux' && (
              <Box>
                <EmtTypography variant="body2" fontWeight="400">
                  Dans un rayon de :
                </EmtTypography>
                <Box sx={{ width: 'calc(100% - 44px)', marginLeft: '22px', marginTop: '46px' }}>
                  <EmtDistance
                    // @ts-expect-error
                    color="purple"
                    min={0}
                    max={100}
                    minDistanceBetweenThumbs={10}
                    value={distancePercent}
                    onChange={(value: number | number[], e?: Event) => {
                      if (isIOS && e?.type === 'mousedown') return
                      handleDistanceLiveChange(value)
                    }}
                    onChangeCommitted={(e: any, value: any) => {
                      // MUI gives (event, value)
                      if (isIOS && e?.type === 'mousedown') return
                      commitDistance(value)
                    }}
                    marks={[
                      { value: 0, label: displayDistance(0) },
                      { value: 10 },
                      { value: 20 },
                      { value: 30 },
                      { value: 40 },
                      { value: 50 },
                      { value: 60 },
                      { value: 70 },
                      { value: 80 },
                      { value: 100, label: displayDistance(50) },
                    ]}
                    valueLabelFormat={(v: number) =>
                      displayDistance(
                        distanceCorrespondingValues.percentToValue[v] || distanceCorrespondingValues.valueToPercent[v],
                        '',
                        1,
                      )
                    }
                    step={10}
                  />
                </Box>
              </Box>
            )} */}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <EmtTypography variant="body1" fontWeight="700" sx={{ fontSize: '18px' }}>
              Pour quels animaux ?
            </EmtTypography>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
              <AnimalIncrement
                slug="dog"
                count={animalCounts.dog}
                onChange={(delta) => handleAnimalChange('dog', delta)}
                showIcon={isMobile || window.matchMedia('(min-width: 1180px)').matches ? true : false}
              />
              <AnimalIncrement
                slug="cat"
                count={animalCounts.cat}
                onChange={(delta) => handleAnimalChange('cat', delta)}
                showIcon={isMobile || window.matchMedia('(min-width: 1180px)').matches ? true : false}
              />
              <AnimalIncrement
                slug="nac"
                count={animalCounts.nac}
                onChange={(delta) => handleAnimalChange('nac', delta)}
                showIcon={isMobile || window.matchMedia('(min-width: 1180px)').matches ? true : false}
              />
            </Box>
            {errors.petsToComplete && (
              <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                {errors.petsToComplete}
              </EmtTypography>
            )}
          </Box>
        </Box>
      </Box>
    )
  },
)

NewSearchForm.displayName = 'NewSearchForm'
export default NewSearchForm
